/*
 * The Office dialog used for sign-in, and the fallbacks around it.
 *
 * The dialog is the primary path: the credential form runs in its own browsing
 * context rather than inside the pane Outlook frames. But the Dialog API is a
 * separate requirement set from Mailbox and not every supported client has it
 * — Outlook on Android and iOS have none of it, and neither does classic
 * Outlook on the web against on-premises Exchange. Rather than exclude those
 * clients in the manifest, the caller falls back to an in-pane form whenever
 * anything here reports the dialog is unavailable.
 *
 * Capability is decided by trying, not by isSetSupported: the requirement-set
 * name would have to be spelled exactly right, and getting it wrong fails
 * closed everywhere instead of only where it should.
 */

/* global Office, URL, window */

import type { Session } from "./session";

/** Raised when the user dismissed the dialog without signing in. */
export class DialogCancelled extends Error {
  constructor() {
    super("Sign-in was cancelled.");
    this.name = "DialogCancelled";
  }
}

/** Raised when the host cannot open a dialog at all, so the caller can inline the form. */
export class DialogUnavailable extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "DialogUnavailable";
  }
}

/** Cheap synchronous check; a true result still does not guarantee success. */
export function canDisplayDialog(): boolean {
  try {
    return (
      typeof Office !== "undefined" && typeof Office.context?.ui?.displayDialogAsync === "function"
    );
  } catch {
    return false;
  }
}

/** 12006 is the documented "user closed the dialog" code. */
const DIALOG_CLOSED_BY_USER = 12006;

/**
 * Opens the sign-in dialog and resolves with the session it sends back.
 *
 * The dialog is same-origin with the pane, so messageParent needs no
 * targetOrigin. It must never be given "*": the message is a session token.
 */
export function openSignInDialog(): Promise<Session> {
  if (!canDisplayDialog()) {
    return Promise.reject(
      new DialogUnavailable("This version of Outlook cannot open add-in dialogs.")
    );
  }

  const url = new URL("dialog.html", window.location.href).href;

  return new Promise<Session>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(url, { height: 60, width: 30 }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new DialogUnavailable(result.error?.message ?? "The dialog could not be opened."));
        return;
      }

      const dialog = result.value;
      let settled = false;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        const message = (arg as { message?: string }).message;
        settled = true;
        dialog.close();
        try {
          resolve(JSON.parse(message ?? "") as Session);
        } catch {
          reject(new Error("The sign-in dialog sent something unreadable."));
        }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
        if (settled) {
          return;
        }
        settled = true;
        const code = (arg as { error?: number }).error;
        reject(
          code === DIALOG_CLOSED_BY_USER
            ? new DialogCancelled()
            : new DialogUnavailable(`The dialog closed unexpectedly (${code ?? "unknown"}).`)
        );
      });
    });
  });
}

/**
 * Sends the session from the dialog back to the task pane.
 *
 * Same origin, so no targetOrigin — and deliberately not "*", which Office
 * allows only for messages that carry nothing sensitive.
 */
export function sendSessionToParent(session: Session): void {
  Office.context.ui.messageParent(JSON.stringify(session));
}

/**
 * Opens a URL outside the add-in. openBrowserAsync hands it to the system
 * browser on the clients that have it; elsewhere a plain window.open is
 * correct and is what the web client does anyway.
 */
export function openExternal(url: string): void {
  try {
    const ui = Office.context?.ui as { openBrowserAsync?: (u: string) => void } | undefined;
    if (typeof ui?.openBrowserAsync === "function") {
      ui.openBrowserAsync(url);
      return;
    }
  } catch {
    // Fall through to window.open.
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
